import { useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { API_ROUTES } from "../../configuration/RoutesApi"
import { useError } from "../../context/useErrorContext"
import { useMessage } from "../../context/useMessageContext"
import { ERequestType } from "../../Enum/ERequestType"
import { AdminLogs } from "../../Objects/AdminDashboard/AdminLogs"
import { DeleteRequest, DownloadFileRequest, GetRequest } from "../../services/ApiClientService"

export const useGetAdminLogs = () => {
  const { t } = useTranslation("admin")

  const { setError } = useError()

  const { setMessage } = useMessage()

  const [isDeletingLog, setIsDeletingLog] = useState(false)

  const [isDownloadingLog, setIsDownloadingLog] = useState(false)

  const latestLogsRequestId = useRef(0)

  const handleGetAdminLogs = async (onSuccess: (data: AdminLogs) => void, silent: boolean = false) => {
    const requestId = ++latestLogsRequestId.current

    try {
      const response = await GetRequest<AdminLogs>(
        `${API_ROUTES.ADMIN_DASHBOARD.GET_LOGS}?includeValidation=true`,
        ERequestType.Admin,
      )

      if (!response.success) {
        throw new Error(response.message)
      }

      if (requestId === latestLogsRequestId.current) {
        onSuccess(new AdminLogs(response.data ?? {}))
      }
    } catch (e: unknown) {
      if (!silent && requestId === latestLogsRequestId.current) {
        const message = e instanceof Error ? e.message : "Erro ao carregar logs do servidor."
        setError("Logs do servidor", message)
      }
    }
  }

  const handleDeleteAdminLog = async (relativePath: string, onSuccess: () => Promise<void>) => {
    try {
      setIsDeletingLog(true)

      const response = await DeleteRequest(API_ROUTES.ADMIN_DASHBOARD.DELETE_LOG(relativePath), ERequestType.Admin)

      if (!response.success) {
        throw new Error(response.message)
      }

      await onSuccess()

      setMessage(t("server_logs_delete_success_title"), t("server_logs_delete_success_message"))
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : t("server_logs_delete_error")
      
      setError(t("server_logs_error_title"), message)
    } finally {
      setIsDeletingLog(false)
    }
  }

  const handleDownloadAdminLog = async (relativePath: string, fileName: string) => {
    try {
      setIsDownloadingLog(true)

      await DownloadFileRequest(API_ROUTES.ADMIN_DASHBOARD.DOWNLOAD_LOG(relativePath), fileName, ERequestType.Admin)
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : t("server_logs_download_error")
      
      setError(t("server_logs_error_title"), message)
    } finally {
      setIsDownloadingLog(false)
    }
  }

  return {
    handleGetAdminLogs,
    handleDeleteAdminLog,
    handleDownloadAdminLog,
    isDeletingLog,
    isDownloadingLog,
  }
}
